/**
 * Well Inventory Panel
 *
 * Lists all PumpAssets (imported + well intelligence pushed).
 * - CSV import for bulk well upload with gauging data
 * - Gauging form per well (bridges to WellGauging for V5 inference)
 * - Status badges, depth, DTW, sat thickness, source
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowPathIcon, ArrowUpTrayIcon, PlusIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/client';
import { readWorkbookSheets, isSupportedTableFile, TABLE_FILE_ACCEPT, type WorkbookResult } from '../../lib/tableImport';

interface PumpAsset {
  id: string;
  well_name: string;
  latitude: number;
  longitude: number;
  well_depth_ft: number;
  ground_elevation_ft: number;
  pump_rate_gpm: number;
  current_dtw_ft: number | null;
  current_sat_ft: number | null;
  last_reading_at: string | null;
  status: string;
  source: string;
  install_date: string | null;
  controller_type: string;
  device_id: string | null;
}

interface Gauging {
  date: string;
  depth_to_water_ft: number | null;
  water_elevation_ft: number | null;
}

interface ReferenceWell {
  well_name: string;
  well_id: string;
  latitude: number;
  longitude: number;
  total_depth_ft?: number | null;
  depth_to_water_ft?: number | null;
  water_elevation_ft?: number | null;
  ground_elevation_ft?: number | null;
}

interface Props {
  siteId: string;
  onWellsChanged?: () => void;
  predictionHorizons?: Record<string, Record<string, number>> | null;
  referenceWells?: ReferenceWell[];
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  installed: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  planned: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  disabled: 'bg-mono-700/50 text-mono-400 border-mono-600',
  abandoned: 'bg-red-500/20 text-red-300 border-red-500/30',
};

const SOURCE_LABELS: Record<string, string> = {
  manual: 'Manual',
  csv_import: 'CSV Import',
  well_intelligence: 'AI Recommended',
};

const HORIZONS = ['3mo', '1yr', '2yr', '5yr', '10yr', '20yr'] as const;

export function WellInventoryPanel({ siteId, onWellsChanged, predictionHorizons, referenceWells = [] }: Props) {
  const [assets, setAssets] = useState<PumpAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);
  const [gaugings, setGaugings] = useState<Gauging[]>([]);
  const [gaugingLoading, setGaugingLoading] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importStatus, setImportStatus] = useState<string | null>(null);
  const [pendingWorkbook, setPendingWorkbook] = useState<WorkbookResult | null>(null);
  const [chosenSheetName, setChosenSheetName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Gauging form state
  const [gaugingDate, setGaugingDate] = useState(new Date().toISOString().slice(0, 10));
  const [gaugingDtw, setGaugingDtw] = useState('');
  const [gaugingWe, setGaugingWe] = useState('');
  const [savingGauging, setSavingGauging] = useState(false);

  // Controller config state
  const [savingConfig, setSavingConfig] = useState(false);

  const loadAssets = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiClient.get(`/phreatic/pump-control/assets/${siteId}`);
      setAssets(resp.data.assets || []);
    } catch { setAssets([]); }
    finally { setLoading(false); }
  }, [siteId]);

  useEffect(() => { loadAssets(); }, [loadAssets]);

  // Load gaugings for selected asset
  useEffect(() => {
    if (!selectedAsset) { setGaugings([]); return; }
    (async () => {
      setGaugingLoading(true);
      try {
        const resp = await apiClient.get(`/phreatic/pump-control/assets/${selectedAsset}/gaugings`);
        setGaugings(resp.data.gaugings || []);
      } catch { setGaugings([]); }
      finally { setGaugingLoading(false); }
    })();
  }, [selectedAsset]);

  // Submit gauging
  async function submitGauging() {
    if (!selectedAsset || (!gaugingDtw && !gaugingWe)) return;
    setSavingGauging(true);
    try {
      await apiClient.post('/phreatic/pump-control/telemetry/manual', {
        site_id: siteId,
        asset_id: selectedAsset,
        depth_to_water_ft: gaugingDtw ? parseFloat(gaugingDtw) : null,
        water_level_ft: gaugingWe ? parseFloat(gaugingWe) : null,
        timestamp: new Date(gaugingDate + 'T12:00:00').toISOString(),
      });
      setGaugingDtw('');
      setGaugingWe('');
      // Refresh gaugings and asset list
      setSelectedAsset(prev => { setTimeout(() => setSelectedAsset(prev), 50); return null; });
      loadAssets();
      onWellsChanged?.();
    } catch (err) {
      console.error('Failed to save gauging:', err);
    } finally {
      setSavingGauging(false);
    }
  }

  // Update controller config
  async function updateControllerConfig(assetId: string, controllerType: string, deviceId?: string, deviceConfig?: Record<string, string>) {
    setSavingConfig(true);
    try {
      await apiClient.put(`/phreatic/pump-control/assets/${assetId}`, {
        controller_type: controllerType,
        device_id: deviceId || null,
        device_config: deviceConfig || null,
      });
      loadAssets();
    } catch (err) {
      console.error('Failed to update controller:', err);
    } finally {
      setSavingConfig(false);
    }
  }

  // Table import handler — accepts Excel/CSV/TSV/ODS. Reads every sheet, then
  // either auto-imports (single sheet) or stages a picker for multi-sheet workbooks.
  async function handleTableFile(file: File) {
    if (!isSupportedTableFile(file)) {
      setImportStatus(`Unsupported file. Use ${TABLE_FILE_ACCEPT}`);
      return;
    }
    setImportStatus(`Reading ${file.name}...`);
    setPendingWorkbook(null);
    setChosenSheetName(null);

    let workbook: WorkbookResult;
    try {
      workbook = await readWorkbookSheets(file);
    } catch (err: any) {
      setImportStatus(`Read failed: ${err?.message || 'unknown error'}`);
      return;
    }

    if (workbook.sheets.length === 1) {
      // Single sheet — proceed directly with the existing flow.
      const sheet = workbook.sheets[0];
      await runCsvImport(sheet.csvText, workbook.sourceFormat, '');
      return;
    }

    // Multi-sheet — show the picker. Pre-select the sheet with the most data
    // rows so the default choice is usually correct.
    const best = [...workbook.sheets].sort((a, b) => b.dataRowCount - a.dataRowCount)[0];
    setPendingWorkbook(workbook);
    setChosenSheetName(best.name);
    setImportStatus(`${workbook.sourceFormat} has ${workbook.sheets.length} sheets — pick one to import`);
  }

  // Pull the parse-and-import step out of handleTableFile so the sheet
  // picker can also invoke it after the user chooses a sheet.
  async function runCsvImport(text: string, sourceLabel: string, sheetInfo: string) {
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      setImportStatus(`${sourceLabel}${sheetInfo} must have a header row + at least one data row`);
      return;
    }

    let wells: any[] = [];

    // Try Gemini AI parsing first (handles any CSV format)
    try {
      setImportStatus(`AI parsing ${sourceLabel}${sheetInfo} (Gemini)...`);
      const parseResp = await apiClient.post('/phreatic/pump-control/assets/parse-csv', {
        csv_text: text,
      });
      wells = parseResp.data.wells || [];
      const notes = parseResp.data.notes;
      if (wells.length > 0) {
        setImportStatus(`AI detected ${wells.length} wells${notes ? ` — ${notes}` : ''}. Importing...`);
      }
    } catch {
      // Gemini unavailable or failed — fall back to manual parsing
      setImportStatus('AI unavailable, using column matching...');
    }

    // Fallback: rigid column-name parser
    if (wells.length === 0) {
      const header = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
      for (let i = 1; i < lines.length; i++) {
        const vals = lines[i].split(',').map(v => v.trim());
        const row: Record<string, string> = {};
        header.forEach((h, j) => { row[h] = vals[j] || ''; });

        const well: any = {
          well_name: row.well_name || row.name || row.well_id || `Well-${i}`,
          latitude: parseFloat(row.latitude || row.lat || '0'),
          longitude: parseFloat(row.longitude || row.lon || row.lng || '0'),
          well_depth_ft: parseFloat(row.well_depth_ft || row.depth || row.total_depth_ft || '300'),
          ground_elevation_ft: parseFloat(row.ground_elevation_ft || row.elevation || '0'),
          pump_rate_gpm: parseFloat(row.pump_rate_gpm || row.gpm || '0'),
          status: row.status || 'active',
        };

        const dtw = parseFloat(row.depth_to_water_ft || row.dtw || '');
        const we = parseFloat(row.water_elevation_ft || row.water_elev || '');
        const gDate = row.gauge_date || row.date || new Date().toISOString().slice(0, 10);
        if (!isNaN(dtw) || !isNaN(we)) {
          well.gauging = [{
            date: gDate,
            depth_to_water_ft: isNaN(dtw) ? null : dtw,
            water_elevation_ft: isNaN(we) ? null : we,
          }];
        }

        if (well.latitude !== 0 && well.longitude !== 0) {
          wells.push(well);
        }
      }
    }

    if (wells.length === 0) {
      setImportStatus('No valid wells found in CSV');
      return;
    }

    setImportStatus(`Importing ${wells.length} wells...`);
    try {
      const resp = await apiClient.post('/phreatic/pump-control/assets/import-csv', {
        site_id: siteId,
        wells,
      });
      setImportStatus(`Imported ${resp.data.imported} wells, ${resp.data.gaugings_bridged} gaugings bridged to V5. Refresh page to generate V5 predictions for imported wells.`);
      loadAssets();
      onWellsChanged?.();
      setPendingWorkbook(null);
      setChosenSheetName(null);
      setTimeout(() => { setShowImport(false); setImportStatus(null); }, 3000);
    } catch (err: any) {
      setImportStatus(`Import failed: ${err?.response?.data?.detail || err.message}`);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <ArrowPathIcon className="w-5 h-5 text-mono-400 animate-spin" />
        <span className="ml-2 text-mono-400 text-sm">Loading well inventory...</span>
      </div>
    );
  }

  const selectedWell = assets.find(a => a.id === selectedAsset);

  return (
    <div className="space-y-3">
      {/* Header + Import button */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-mono-400">
          {assets.length} well{assets.length !== 1 ? 's' : ''} in inventory
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowImport(!showImport)}
            className="flex items-center gap-1 px-3 py-1 text-xs rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30">
            <ArrowUpTrayIcon className="w-3.5 h-3.5" />
            Import
          </button>
        </div>
      </div>

      {/* CSV Import panel */}
      {showImport && (
        <div className="bg-mono-900 border border-cyan-500/30 rounded-lg p-4 space-y-3">
          <div className="text-xs text-mono-300">
            Upload a well inventory — <span className="text-cyan-400">AI auto-detects columns</span> (Gemini).
            Accepts <span className="text-cyan-400">Excel (.xlsx, .xls, .xlsm, .xlsb), CSV, TSV, ODS</span>.
            <br />Needs at minimum: <span className="text-mono-400">well name, lat/lon, depth. Gauging data (DTW, water elevation, dates) imported automatically if present. Multi-sheet workbooks use the first sheet.</span>
          </div>
          <div className="flex items-center gap-3">
            <input ref={fileInputRef} type="file" accept={TABLE_FILE_ACCEPT} className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) handleTableFile(f); }} />
            <button onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1 px-4 py-1.5 text-xs rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30">
              <ArrowUpTrayIcon className="w-3.5 h-3.5" />
              Choose File
            </button>
            {importStatus && <span className="text-xs text-mono-400">{importStatus}</span>}
          </div>

          {/* Multi-sheet picker — only shown when the workbook has 2+ sheets */}
          {pendingWorkbook && pendingWorkbook.sheets.length > 1 && (
            <div className="border-t border-mono-700 pt-3 space-y-2">
              <div className="text-xs text-mono-300">
                Pick the sheet to import. We default to the one with the most data rows.
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <select
                  value={chosenSheetName ?? ''}
                  onChange={e => setChosenSheetName(e.target.value)}
                  className="bg-mono-800 border border-mono-600 text-mono-100 text-xs rounded px-2 py-1.5 focus:outline-none focus:border-cyan-500"
                >
                  {pendingWorkbook.sheets.map((s) => (
                    <option key={s.name} value={s.name}>
                      {s.name} ({s.dataRowCount} row{s.dataRowCount === 1 ? '' : 's'})
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    if (!pendingWorkbook || !chosenSheetName) return;
                    const sheet = pendingWorkbook.sheets.find(s => s.name === chosenSheetName);
                    if (!sheet) return;
                    runCsvImport(sheet.csvText, pendingWorkbook.sourceFormat, ` (sheet "${sheet.name}")`);
                  }}
                  className="flex items-center gap-1 px-4 py-1.5 text-xs rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30"
                >
                  Import this sheet
                </button>
                <button
                  onClick={() => { setPendingWorkbook(null); setChosenSheetName(null); setImportStatus(null); }}
                  className="text-xs text-mono-400 hover:text-mono-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Well list */}
      {assets.length === 0 ? (
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-6 text-center">
          <p className="text-mono-400 text-sm">No wells in inventory.</p>
          <p className="text-mono-500 text-xs mt-1">Import existing wells via CSV or add wells from Well Intelligence.</p>
        </div>
      ) : (
        <div className="bg-mono-900 border border-mono-800 rounded-lg overflow-hidden overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-mono-400 border-b border-mono-800 bg-mono-950/50">
                <th className="text-left py-2 px-3 sticky left-0 bg-mono-950">Well</th>
                <th className="text-right py-2 px-2">Depth</th>
                <th className="text-right py-2 px-2">DTW</th>
                <th className="text-right py-2 px-2">Sat</th>
                <th className="text-right py-2 px-2">GPM</th>
                <th className="text-center py-2 px-2">Status</th>
                <th className="text-center py-2 px-2">Source</th>
                {HORIZONS.map(h => (
                  <th key={h} className="text-right py-2 px-2 text-cyan-500/70">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {assets.map(a => {
                const preds = predictionHorizons?.[a.well_name] || predictionHorizons?.[`\u2b24 ${a.well_name}`];
                const ge = a.ground_elevation_ft || 0;
                const wd = a.well_depth_ft || 300;
                return (
                <tr key={a.id}
                  onClick={() => setSelectedAsset(selectedAsset === a.id ? null : a.id)}
                  className={`border-b border-mono-800/50 cursor-pointer transition-colors ${
                    selectedAsset === a.id ? 'bg-cyan-500/5' : 'hover:bg-mono-800/30'
                  }`}
                >
                  <td className="py-2 px-3 text-mono-200 font-medium sticky left-0 bg-mono-900">{a.well_name}</td>
                  <td className="text-right py-2 px-2 text-mono-300">{a.well_depth_ft}</td>
                  <td className="text-right py-2 px-2 text-mono-300">{a.current_dtw_ft?.toFixed(1) ?? '—'}</td>
                  <td className="text-right py-2 px-2 text-cyan-400">{a.current_sat_ft?.toFixed(1) ?? '—'}</td>
                  <td className="text-right py-2 px-2 text-mono-300">{a.pump_rate_gpm || '—'}</td>
                  <td className="text-center py-2 px-2">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] border ${STATUS_COLORS[a.status] || STATUS_COLORS.active}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="text-center py-2 px-2 text-mono-500 text-[10px]">
                    {SOURCE_LABELS[a.source] || a.source}
                  </td>
                  {HORIZONS.map(h => {
                    const predElev = preds?.[h];
                    if (!predElev) return <td key={h} className="text-right py-2 px-2 text-mono-600">—</td>;
                    const predDtw = ge > 0 ? ge - predElev : null;
                    const predSat = predDtw != null ? Math.max(0, wd - predDtw) : null;
                    return (
                      <td key={h} className="text-right py-2 px-2 text-cyan-300">
                        {predSat != null ? predSat.toFixed(0) : predElev.toFixed(1)}
                      </td>
                    );
                  })}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Selected well detail + config + gauging */}
      {selectedWell && (
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-mono-200">{selectedWell.well_name}</h4>
            <div className="flex items-center gap-3">
              {selectedWell.last_reading_at && (
                <span className="text-[9px] text-mono-500">
                  Last reading: {new Date(selectedWell.last_reading_at).toLocaleDateString()}
                </span>
              )}
              <span className="text-[10px] text-mono-500">
                {selectedWell.latitude.toFixed(6)}, {selectedWell.longitude.toFixed(6)}
              </span>
            </div>
          </div>

          {/* ── Data Source Configuration ── */}
          <div className="border-t border-mono-800 pt-2">
            <div className="text-[10px] text-mono-500 uppercase tracking-wider mb-2">Data Source</div>
            <div className="flex items-center gap-3">
              {(['none', 'manual', 'aqvify', 'hobonet', 'mqtt'] as const).map(ct => {
                const labels: Record<string, string> = {
                  none: 'Not Configured',
                  manual: 'Manual / CSV',
                  aqvify: 'Aqvify API',
                  hobonet: 'HOBOnet',
                  mqtt: 'MQTT / Holykell',
                };
                const isActive = (selectedWell.controller_type || 'none') === ct;
                return (
                  <button key={ct}
                    onClick={() => updateControllerConfig(selectedWell.id, ct)}
                    disabled={savingConfig}
                    className={`px-2.5 py-1 text-[10px] rounded border transition-colors ${
                      isActive
                        ? ct === 'none' ? 'bg-mono-700/50 text-mono-300 border-mono-600'
                          : ct === 'manual' ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40'
                          : 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40'
                        : 'bg-mono-800/50 text-mono-500 border-mono-700 hover:text-mono-300 hover:border-mono-500'
                    }`}
                  >
                    {labels[ct]}
                  </button>
                );
              })}
            </div>

            {/* Telemetry adapter config fields */}
            {selectedWell.controller_type && !['none', 'manual'].includes(selectedWell.controller_type) && (
              <TelemetryConfigForm
                assetId={selectedWell.id}
                controllerType={selectedWell.controller_type}
                deviceId={selectedWell.device_id}
                onSave={(deviceId, config) => updateControllerConfig(selectedWell.id, selectedWell.controller_type, deviceId, config)}
                saving={savingConfig}
              />
            )}

            {/* Status indicator for connected wells */}
            {selectedWell.controller_type && !['none', 'manual'].includes(selectedWell.controller_type) && (
              <div className="mt-2 flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${selectedWell.last_reading_at ? 'bg-cyan-400' : 'bg-red-400'}`} />
                <span className="text-[10px] text-mono-400">
                  {selectedWell.last_reading_at
                    ? `Connected — last reading ${new Date(selectedWell.last_reading_at).toLocaleString()}`
                    : 'No readings yet — check device configuration'}
                </span>
              </div>
            )}
          </div>

          {/* ── Gauging History ── */}
          <div className="border-t border-mono-800 pt-2">
            <div className="text-[10px] text-mono-500 uppercase tracking-wider mb-1">
              Gauging History
              {selectedWell.controller_type && !['none', 'manual'].includes(selectedWell.controller_type) && (
                <span className="ml-2 text-cyan-400/60">(auto-populated by telemetry)</span>
              )}
            </div>
            {gaugingLoading ? (
              <div className="text-mono-500 text-xs animate-pulse">Loading...</div>
            ) : gaugings.length === 0 ? (
              <div className="text-mono-500 text-xs">No gauging records.</div>
            ) : (
              <div className="max-h-40 overflow-y-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-mono-500 border-b border-mono-800">
                      <th className="text-left py-1 px-2">Date</th>
                      <th className="text-right py-1 px-2">DTW (ft)</th>
                      <th className="text-right py-1 px-2">Water Elev (ft)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gaugings.map((g, i) => (
                      <tr key={i} className="border-b border-mono-800/30 text-mono-300">
                        <td className="py-1 px-2">{g.date}</td>
                        <td className="text-right py-1 px-2">{g.depth_to_water_ft?.toFixed(1) ?? '—'}</td>
                        <td className="text-right py-1 px-2">{g.water_elevation_ft?.toFixed(1) ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Manual Gauging Form (only for manual/none wells) ── */}
          {(!selectedWell.controller_type || ['none', 'manual'].includes(selectedWell.controller_type)) && (
            <div className="border-t border-mono-800 pt-2">
              <div className="text-[10px] text-mono-500 uppercase tracking-wider mb-1">Add Gauging Reading</div>
              <div className="flex items-end gap-2">
                <div>
                  <label className="text-[9px] text-mono-500">Date</label>
                  <input type="date" value={gaugingDate} onChange={e => setGaugingDate(e.target.value)}
                    className="block w-full mt-0.5 px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200 focus:border-cyan-500 focus:outline-none" />
                </div>
                <div>
                  <label className="text-[9px] text-mono-500">DTW (ft)</label>
                  <input type="number" value={gaugingDtw} onChange={e => setGaugingDtw(e.target.value)} placeholder="0.0" step="0.1"
                    className="block w-full mt-0.5 px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200 focus:border-cyan-500 focus:outline-none" />
                </div>
                <div>
                  <label className="text-[9px] text-mono-500">Water Elev (ft)</label>
                  <input type="number" value={gaugingWe} onChange={e => setGaugingWe(e.target.value)} placeholder="0.0" step="0.1"
                    className="block w-full mt-0.5 px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200 focus:border-cyan-500 focus:outline-none" />
                </div>
                <button onClick={submitGauging} disabled={savingGauging || (!gaugingDtw && !gaugingWe)}
                  className="flex items-center gap-1 px-3 py-1 text-xs rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 disabled:opacity-50 whitespace-nowrap">
                  <PlusIcon className="w-3 h-3" />
                  {savingGauging ? 'Saving...' : 'Add'}
                </button>
              </div>
              <div className="text-[9px] text-mono-600 mt-1">
                Gauging data is bridged to V5 inference automatically.
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Reference Wells Table ── */}
      {referenceWells.length > 0 && (
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
          <h3 className="text-xs font-semibold text-mono-400 uppercase tracking-wider mb-3">
            Reference Wells — V5 Predicted Water Elevation (ft)
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-mono-400 border-b border-mono-800">
                  <th className="text-left py-1.5 px-2 sticky left-0 bg-mono-900">Well</th>
                  <th className="text-right py-1.5 px-2">Depth</th>
                  <th className="text-right py-1.5 px-2">DTW</th>
                  <th className="text-right py-1.5 px-2">Elev</th>
                  <th className="text-right py-1.5 px-2">Sat</th>
                  {HORIZONS.map(h => (
                    <th key={h} className="text-right py-1.5 px-2 text-cyan-500/70">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {referenceWells.filter(w => w.depth_to_water_ft != null || w.water_elevation_ft != null).map(w => {
                  const preds = predictionHorizons?.[w.well_name];
                  const ge = w.ground_elevation_ft ?? 0;
                  const wd = w.total_depth_ft ?? 300;
                  const dtw = w.depth_to_water_ft;
                  const sat = dtw != null ? Math.max(0, wd - dtw) : null;
                  return (
                    <tr key={w.well_id || w.well_name} className="border-b border-mono-800/30">
                      <td className="py-1.5 px-2 text-mono-300 font-medium sticky left-0 bg-mono-900">{w.well_name}</td>
                      <td className="text-right py-1.5 px-2 text-mono-400">{wd}</td>
                      <td className="text-right py-1.5 px-2 text-mono-300">{dtw?.toFixed(1) ?? '—'}</td>
                      <td className="text-right py-1.5 px-2 text-mono-300">{w.water_elevation_ft?.toFixed(1) ?? '—'}</td>
                      <td className="text-right py-1.5 px-2 text-cyan-400">{sat?.toFixed(0) ?? '—'}</td>
                      {HORIZONS.map(h => {
                        const predElev = preds?.[h];
                        if (!predElev) return <td key={h} className="text-right py-1.5 px-2 text-mono-600">—</td>;
                        const predDtw = ge > 0 ? ge - predElev : null;
                        const predSat = predDtw != null ? Math.max(0, wd - predDtw) : null;
                        const declining = w.water_elevation_ft != null && predElev < w.water_elevation_ft;
                        return (
                          <td key={h} className={`text-right py-1.5 px-2 ${declining ? 'text-cyan-400' : 'text-cyan-300'}`}>
                            {predElev.toFixed(1)}
                            {predSat != null && <span className="text-[9px] text-mono-500 ml-0.5">({predSat.toFixed(0)})</span>}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-[9px] text-mono-600 mt-2">
            Horizon values = predicted water elevation. Parentheses = predicted sat thickness. Amber = declining.
          </div>
        </div>
      )}
    </div>
  );
}

// ── Telemetry Adapter Config Form ──

const ADAPTER_FIELDS: Record<string, { label: string; fields: { key: string; label: string; placeholder: string; secret?: boolean }[] }> = {
  aqvify: {
    label: 'Aqvify REST API',
    fields: [
      { key: 'api_key', label: 'API Key', placeholder: 'aqv_...', secret: true },
      { key: 'base_url', label: 'Base URL', placeholder: 'https://api.aqvify.com/v1' },
    ],
  },
  hobonet: {
    label: 'HOBOnet (Onset)',
    fields: [
      { key: 'client_id', label: 'Client ID', placeholder: 'hobo-client-...' },
      { key: 'client_secret', label: 'Client Secret', placeholder: 'hobo-secret-...', secret: true },
      { key: 'station_serial', label: 'Station Serial', placeholder: 'SN-12345' },
    ],
  },
  mqtt: {
    label: 'MQTT / Holykell',
    fields: [
      { key: 'broker_url', label: 'Broker URL', placeholder: 'mqtt://broker.example.com:1883' },
      { key: 'topic_prefix', label: 'Topic Prefix', placeholder: 'wells/site-01/' },
      { key: 'username', label: 'Username', placeholder: 'device-user' },
      { key: 'password', label: 'Password', placeholder: '...', secret: true },
    ],
  },
};

function TelemetryConfigForm({ assetId, controllerType, deviceId, onSave, saving }: {
  assetId: string;
  controllerType: string;
  deviceId: string | null;
  onSave: (deviceId: string, config: Record<string, string>) => void;
  saving: boolean;
}) {
  const adapter = ADAPTER_FIELDS[controllerType];
  const [fields, setFields] = React.useState<Record<string, string>>({});
  const [devId, setDevId] = React.useState(deviceId || '');

  if (!adapter) return null;

  return (
    <div className="mt-2 bg-mono-800/30 rounded p-3 space-y-2">
      <div className="text-[10px] text-mono-400 font-semibold">{adapter.label} Configuration</div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[9px] text-mono-500">Device ID</label>
          <input type="text" value={devId} onChange={e => setDevId(e.target.value)}
            placeholder="unique-device-id"
            className="block w-full mt-0.5 px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200 focus:border-cyan-500 focus:outline-none" />
        </div>
        {adapter.fields.map(f => (
          <div key={f.key}>
            <label className="text-[9px] text-mono-500">{f.label}</label>
            <input
              type={f.secret ? 'password' : 'text'}
              value={fields[f.key] || ''}
              onChange={e => setFields(prev => ({ ...prev, [f.key]: e.target.value }))}
              placeholder={f.placeholder}
              className="block w-full mt-0.5 px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200 focus:border-cyan-500 focus:outline-none"
            />
          </div>
        ))}
      </div>
      <button
        onClick={() => onSave(devId, fields)}
        disabled={saving || !devId}
        className="flex items-center gap-1 px-3 py-1 text-xs rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 disabled:opacity-50"
      >
        {saving ? 'Saving...' : 'Save Configuration'}
      </button>
      <div className="text-[9px] text-mono-600">
        When configured, telemetry will be polled automatically every 5 minutes and bridged to V5 inference.
      </div>
    </div>
  );
}
